<ng-container *transloco="let t">
  @if (device) {
    <div class="min-h-full bg-page text-ink-1">
      <!-- ═══════════════ HEADER ═══════════════ -->
      <header class="border-b border-hairline bg-card">
        <div class="mx-auto w-full max-w-[1240px] px-8 pt-5">
          <!-- title row (breadcrumb trail is rendered globally by app-breadcrumbs) -->
          <div class="flex flex-wrap items-start justify-between gap-6">
            <div class="flex flex-col gap-2.5">
              <div class="flex flex-wrap items-center gap-3">
                <h1 class="m-0 font-num text-2xl font-bold tracking-tight">{{ device.clientId }}</h1>

                @if (device.online) {
                  <app-label-badge dot="green">{{ t('devices.status.online') }}</app-label-badge>
                } @else {
                  <app-label-badge dot="red">{{ t('devices.status.offline') }}</app-label-badge>
                }

                @if (device.isProvisioned) {
                  <app-label-badge>{{ t('devices.status.provisioned') }}</app-label-badge>
                }
                @if (device.waitingForReset) {
                  <app-label-badge dot="orange">{{ t('devices.status.awaitingReset') }}</app-label-badge>
                }
              </div>

              <!-- identity line -->
              <div class="flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-3">
                <span
                  >{{ t('devices.columns.serialNumber') }}
                  <span class="font-num text-ink-1">{{ device.serialNumber ?? t('common.na') }}</span></span
                >
                <span
                  >{{ t('devices.columns.vendorId') }} <span class="text-ink-1">{{ device.vendorId }}</span></span
                >
                <span
                  >{{ t('devices.columns.type') }} <span class="text-ink-1">{{ device.type }}</span></span
                >
                @if (locationName()) {
                  <span class="text-ink-1">{{ locationName() }}</span>
                }
                @if (heartbeat?.lastHeartbeatAt) {
                  <span
                    >{{ t('devices.view.lastHeartbeat') }}
                    <span class="text-ink-1">{{ heartbeat!.lastHeartbeatAt | date: 'medium' }}</span></span
                  >
                }
                @if (status?.createdAt) {
                  <span
                    >{{ t('devices.view.lastStatus') }}
                    <span class="text-ink-1">{{ status!.createdAt | date: 'medium' }}</span></span
                  >
                }
              </div>
            </div>

            <!-- primary actions — grouped by blast radius: navigation/assignment,
                 then destructive — with a hairline divider between groups. -->
            <div class="flex shrink-0 flex-wrap items-center gap-2.5 pt-0.5">
              <app-button variant="primary" size="sm" [label]="assignContentLabel()" (activated)="openAssignContent()" />

              <div class="h-5 w-px bg-hairline"></div>

              <app-button variant="secondary" size="sm" destructive [label]="t('devices.view.actions.reset')" (activated)="openResetModal()" />
              <app-button variant="primary" size="sm" destructive [label]="t('devices.view.actions.remove')" (activated)="openRemoveModal()" />
            </div>
          </div>

          <!-- tabs -->
          <div class="mt-4 flex gap-1" role="tablist">
            @for (tab of tabs; track tab) {
              <button
                type="button"
                role="tab"
                [attr.aria-selected]="activeTab() === tab"
                (click)="selectTab(tab)"
                class="-mb-px border-b-2 px-4 pb-3 pt-2.5 text-sm font-semibold transition-colors"
                [class]="
                  activeTab() === tab
                    ? 'border-brand text-ink-1'
                    : 'border-transparent text-ink-3 hover:text-ink-2'
                "
              >
                {{ t('devices.view.tabs.' + tab) }}
                @if (tab === 'activity' && messages.length) {
                  <span class="ml-1.5 font-num text-xs text-ink-3">{{ messages.length }}</span>
                }
              </button>
            }
          </div>
        </div>
      </header>

      <!-- ═══════════════ CONTENT ═══════════════ -->
      <div class="mx-auto w-full max-w-[1240px] px-8 pb-12 pt-7">
        @if (isLoading) {
          <div class="flex items-center justify-center py-24 text-ink-3">
            {{ t('devices.view.loading') }}
          </div>
        } @else {
          <!-- ───────── OVERVIEW ───────── -->
          @if (activeTab() === 'overview') {
            <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-[440px_1fr]">
              <!-- live image -->
              <section class="overflow-hidden rounded-card border border-hairline bg-card">
                <div class="flex items-center justify-between border-b border-hairline px-5 py-3.5">
                  <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-semibold">{{ t('devices.view.liveImage.title') }}</span>
                    @if (displaySnapshot?.uploadedAt) {
                      <span class="text-xs text-ink-3">{{
                        t('devices.view.liveImage.captured', {
                          time: (displaySnapshot!.uploadedAt | date: 'short'),
                        })
                      }}</span>
                    }
                  </div>
                  <button
                    type="button"
                    (click)="requestSnapshot()"
                    [disabled]="isRequestingSnapshot"
                    class="rounded-control border border-strong bg-card px-3 py-1.5 text-xs font-semibold hover:bg-sunken disabled:opacity-60"
                  >
                    {{ isRequestingSnapshot ? t('devices.view.liveImage.requesting') : t('devices.view.liveImage.request') }}
                  </button>
                </div>
                <div class="flex items-center justify-center bg-inverse p-7">
                  @if (displaySnapshot && snapshotImageUrls[displaySnapshot.id]) {
                    <img
                      [src]="snapshotImageUrls[displaySnapshot.id]"
                      [alt]="t('devices.detail.latestImageAlt')"
                      (click)="viewSnapshot(displaySnapshot!)"
                      class="max-h-[360px] w-full cursor-pointer rounded object-contain"
                    />
                  } @else {
                    <div class="flex h-48 w-full items-center justify-center text-sm text-ink-3">
                      @if (isLoadingLatestSnapshot) {
                        {{ t('devices.detail.latestImagePending') }}
                      } @else {
                        {{ t('devices.detail.latestImageNone') }}
                      }
                    </div>
                  }
                </div>
              </section>

              <!-- right column -->
              <div class="flex flex-col gap-6">
                <!-- vitals -->
                <div class="grid grid-cols-2 gap-3.5 sm:grid-cols-4">
                  <!-- battery -->
                  <div class="flex flex-col gap-2 rounded-card border border-hairline bg-card p-4">
                    <span class="text-xs font-semibold uppercase tracking-wide text-ink-3">{{
                      t('devices.view.vitals.battery')
                    }}</span>
                    <span class="font-num text-2xl font-bold"
                      >{{ heartbeat?.batteryCapacity ?? '—' }}<span class="text-sm">%</span></span
                    >
                    <div class="h-1.5 overflow-hidden rounded-pill bg-sunken">
                      <div
                        class="h-full rounded-pill"
                        [style.width.%]="heartbeat?.batteryCapacity ?? 0"
                        [class]="
                          {
                            good: 'bg-success',
                            warn: 'bg-warning',
                            bad: 'bg-danger',
                            none: 'bg-transparent',
                          }[batteryTone(heartbeat?.batteryCapacity)]
                        "
                      ></div>
                    </div>
                    <span class="font-num text-xs text-ink-3">{{
                      heartbeat?.batteryVoltage != null ? heartbeat!.batteryVoltage + ' V' : t('common.na')
                    }}</span>
                  </div>

                  <!-- signal -->
                  <div class="flex flex-col gap-2 rounded-card border border-hairline bg-card p-4">
                    <span class="text-xs font-semibold uppercase tracking-wide text-ink-3">{{
                      t('devices.view.vitals.signal')
                    }}</span>
                    <span class="font-num text-2xl font-bold"
                      >{{ heartbeat?.signalStrength ?? '—' }}
                      <span class="text-sm font-medium text-ink-3">dBm</span></span
                    >
                    <span
                      class="text-xs font-semibold"
                      [class]="
                        {
                          good: 'text-success',
                          fair: 'text-warning',
                          poor: 'text-danger',
                          none: 'text-ink-3',
                        }[signalQuality(heartbeat?.signalStrength)]
                      "
                      >{{ signalQualityLabel(heartbeat?.signalStrength) }}</span
                    >
                  </div>

                  <!-- uptime -->
                  <div class="flex flex-col gap-2 rounded-card border border-hairline bg-card p-4">
                    <span class="text-xs font-semibold uppercase tracking-wide text-ink-3">{{
                      t('devices.view.vitals.uptime')
                    }}</span>
                    <span class="font-num text-2xl font-bold">{{ formatUptime(heartbeat?.uptime) }}</span>
                    @if (status?.upSince) {
                      <span class="mt-auto text-xs text-ink-3">{{
                        t('devices.view.vitals.upSince', { time: (status!.upSince | date: 'short') })
                      }}</span>
                    }
                  </div>

                  <!-- temperature -->
                  <div class="flex flex-col gap-2 rounded-card border border-hairline bg-card p-4">
                    <span class="text-xs font-semibold uppercase tracking-wide text-ink-3">{{
                      t('devices.view.vitals.temperature')
                    }}</span>
                    <span class="font-num text-2xl font-bold"
                      >{{ heartbeat?.temperature | measure: 'temperature': 'value'
                      }}<span class="text-sm">{{ null | measure: 'temperature': 'unit' }}</span></span
                    >
                    <span class="mt-auto text-xs text-ink-3">{{ t('devices.view.vitals.enclosure') }}</span>
                  </div>
                </div>

                <!-- location -->
                <section class="overflow-hidden rounded-card border border-hairline bg-card">
                  <div class="grid grid-cols-1 md:grid-cols-[1fr_300px]">
                    <div class="flex flex-col gap-3.5 p-5">
                      <div class="flex flex-col gap-0.5">
                        <span class="text-sm font-semibold">{{ t('devices.view.location.title') }}</span>
                        @if (locationName()) {
                          <span class="text-sm text-ink-3">{{ locationName() }}</span>
                        }
                      </div>
                      <div class="flex flex-col gap-2.5 text-sm">
                        <div class="flex items-start gap-2.5">
                          <span class="w-16 shrink-0 text-xs text-ink-3">{{
                            t('devices.view.location.localities')
                          }}</span>
                          <div class="flex flex-wrap gap-1.5">
                            @for (binding of device.contentBindings; track binding.tenantId + '-' + binding.feedId + '-' + binding.resourceId) {
                              <span class="rounded-control border border-hairline bg-sunken px-2.5 py-0.5 text-xs">{{
                                binding.resourceId
                              }}</span>
                            } @empty {
                              <span class="text-ink-3">{{ t('common.na') }}</span>
                            }
                          </div>
                        </div>
                        <div class="flex items-center gap-2.5">
                          <span class="w-16 shrink-0 text-xs text-ink-3">{{
                            t('devices.view.location.coords')
                          }}</span>
                          <span class="font-num text-xs"
                            >{{ device.latitude ?? t('common.na') }},
                            {{ device.longitude ?? t('common.na') }}</span
                          >
                        </div>
                      </div>
                    </div>
                    @if (mapOptions) {
                      <div
                        class="min-h-[180px] border-t border-hairline md:border-l md:border-t-0"
                        leaflet
                        [leafletOptions]="mapOptions"
                        [leafletLayers]="mapLayers"
                        (leafletMapReady)="onMapReady($event)"
                      ></div>
                    }
                  </div>
                </section>
              </div>
            </div>
          }

          <!-- ───────── CONTENT ───────── -->
          @if (activeTab() === 'content') {
            <section class="overflow-hidden rounded-card border border-hairline bg-card">
              <div class="border-b border-hairline px-5 py-3.5">
                <span class="block text-sm font-semibold">{{ t('devices.view.content.title') }}</span>
                <span
                  class="block text-xs"
                  [class]="contentSummary().hasContent ? 'text-ink-3' : 'text-danger'"
                  >{{ contentSummary().subtitle }}</span
                >
              </div>

              @if (contentSummary().hasContent) {
                <div class="flex flex-wrap gap-4 p-5">
                  @for (layout of contentSummary().layouts; track layout.id) {
                    <div class="w-[150px]">
                      <div
                        class="relative w-full overflow-hidden rounded bg-inverse"
                        style="aspect-ratio: 1448 / 1072"
                      >
                        @for (rect of layoutComponentRects(layout); track $index) {
                          <div
                            class="absolute flex items-center justify-center overflow-hidden bg-white/[.16] p-1 shadow-[inset_0_0_0_1px_rgba(255,255,255,.45)]"
                            [style.left]="rect.left"
                            [style.top]="rect.top"
                            [style.width]="rect.width"
                            [style.height]="rect.height"
                          >
                            @if (rect.showLabel) {
                              <span
                                class="rounded-sm bg-inverse/70 px-1 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wide text-white"
                                >{{ rect.label }}</span
                              >
                            }
                          </div>
                        }
                      </div>
                      <p class="m-0 mt-1.5 text-[13px] font-semibold leading-[18px]">{{ layoutEventLabel(layout) }}</p>
                      <p class="m-0 text-xs leading-4 text-ink-3">{{ layoutDetailLine(layout) }}</p>
                    </div>
                  }
                </div>
              } @else {
                <div class="px-5 py-8 text-center">
                  <p class="m-0 mb-1 text-sm font-semibold">{{ t('devices.view.content.emptyTitle') }}</p>
                  <p class="m-0 text-[13px] text-ink-3">{{ t('devices.view.content.emptyBody') }}</p>
                </div>
              }
            </section>
          }

          <!-- ───────── TERMINAL ───────── -->
          @if (activeTab() === 'terminal') {
            <app-terminal [clientId]="device.clientId" />
          }

          <!-- ───────── SYSTEM ───────── -->
          @if (activeTab() === 'system') {
            <div class="mb-4 flex items-center justify-end gap-3">
              @if (status?.updatedAt) {
                <span class="text-xs text-ink-3">{{
                  t('devices.view.statusFetched', { time: (status!.updatedAt | date: 'short') })
                }}</span>
              }
              <button
                type="button"
                (click)="requestStatus()"
                [disabled]="isRequestingStatus"
                class="rounded-control border border-strong bg-card px-3.5 py-1.5 text-sm font-semibold hover:bg-sunken disabled:opacity-60"
              >
                {{ isRequestingStatus ? t('devices.view.actions.refreshing') : t('devices.view.actions.refreshStatus') }}
              </button>
            </div>
            <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
              <ng-container
                *ngTemplateOutlet="detailCard; context: { title: t('devices.view.os.title'), rows: osRows() }"
              />
              <ng-container
                *ngTemplateOutlet="
                  detailCard;
                  context: { title: t('devices.view.resources.title'), rows: resourceRows() }
                "
              />
              <ng-container
                *ngTemplateOutlet="detailCard; context: { title: t('devices.view.env.title'), rows: envRows() }"
              />
            </div>
          }

          <!-- ───────── NETWORK ───────── -->
          @if (activeTab() === 'network') {
            <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
              <ng-container
                *ngTemplateOutlet="detailCard; context: { title: t('devices.view.net.title'), rows: connRows() }"
              />
              <!-- signal card -->
              <section class="rounded-card border border-hairline bg-card">
                <div class="border-b border-hairline px-5 py-3.5 text-sm font-semibold">
                  {{ t('devices.view.signal.title') }}
                </div>
                <div class="flex flex-col gap-3.5 px-5 py-4">
                  <div class="flex items-baseline gap-2.5">
                    <span class="font-num text-3xl font-bold">{{
                      status?.signalStrength ?? heartbeat?.signalStrength ?? '—'
                    }}</span>
                    <span class="text-sm text-ink-3">{{ t('devices.view.signal.strength') }}</span>
                    <span
                      class="ml-auto rounded-control px-2.5 py-0.5 text-xs font-semibold"
                      [class]="
                        {
                          good: 'bg-success-surface text-success',
                          fair: 'bg-warning-surface text-warning',
                          poor: 'bg-danger-surface text-danger',
                          none: 'bg-sunken text-ink-3',
                        }[signalQuality(status?.signalStrength ?? heartbeat?.signalStrength)]
                      "
                      >{{ signalQualityLabel(status?.signalStrength ?? heartbeat?.signalStrength) }}</span
                    >
                  </div>
                  <div class="flex justify-between border-t border-hairline pt-3 text-sm">
                    <span class="text-ink-3">{{ t('devices.view.signal.power') }}</span>
                    <span class="font-num">{{
                      status?.signalPower != null ? status!.signalPower + ' dBm' : t('common.na')
                    }}</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-ink-3">{{ t('devices.view.signal.quality') }}</span>
                    <span class="font-num">{{
                      status?.signalQuality != null ? status!.signalQuality + ' dB' : t('common.na')
                    }}</span>
                  </div>
                </div>
              </section>
              <ng-container
                *ngTemplateOutlet="detailCard; context: { title: t('devices.view.modem.title'), rows: modemRows() }"
              />
            </div>
          }

          <!-- ───────── DISPLAY ───────── -->
          @if (activeTab() === 'display') {
            <div class="mb-4 flex justify-end">
              <button
                type="button"
                (click)="requestCapability()"
                [disabled]="isRequestingCapability"
                class="rounded-control border border-strong bg-card px-3.5 py-1.5 text-sm font-semibold hover:bg-sunken disabled:opacity-60"
              >
                {{ t('devices.view.actions.refreshCapabilities') }}
              </button>
            </div>
            <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
              <div class="flex flex-col gap-6">
                <ng-container
                  *ngTemplateOutlet="detailCard; context: { title: t('devices.view.screen.title'), rows: screenRows() }"
                />
                <ng-container
                  *ngTemplateOutlet="detailCard; context: { title: t('devices.view.text.title'), rows: textRows() }"
                />
              </div>
              <div class="flex flex-col gap-6">
                <!-- content support -->
                <section class="rounded-card border border-hairline bg-card">
                  <div class="border-b border-hairline px-5 py-3.5 text-sm font-semibold">
                    {{ t('devices.view.caps.title') }}
                  </div>
                  <div class="flex flex-wrap gap-2 px-5 py-4">
                    @for (chip of capabilityChips(); track chip.label) {
                      <app-label-badge [dot]="chip.supported ? 'green' : null">
                        <span aria-hidden="true">{{ chip.supported ? '✓' : '✕' }}</span>{{ chip.label }}
                      </app-label-badge>
                    }
                  </div>
                </section>
                <!-- hardware config -->
                <section class="rounded-card border border-hairline bg-card">
                  <div class="border-b border-hairline px-5 py-3.5 text-sm font-semibold">
                    {{ t('devices.view.hardware.title') }}
                  </div>
                  <div class="px-5 pb-1 pt-2">
                    @for (row of hardwareRows(); track row.key) {
                      <div class="flex justify-between gap-4 border-b border-hairline py-2.5 text-sm last:border-b-0">
                        <span class="text-ink-3">{{ row.key }}</span>
                        <span class="break-all text-right font-num">{{ row.value }}</span>
                      </div>
                    }
                  </div>

                  @if (configuration?.displayPorts?.length) {
                    <div class="flex flex-col gap-4 px-5 pb-5 pt-3">
                      @for (port of configuration!.displayPorts; track port.id) {
                        <div>
                          <div class="mb-2 text-xs font-semibold text-ink-3">
                            {{ t('devices.view.hardware.port', { name: port.portName }) }}
                          </div>
                          <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                            <div class="rounded-control border border-hairline bg-sunken px-3 py-2.5">
                              <div class="text-xs text-ink-3">{{ t('devices.view.hardware.vcom') }}</div>
                              <div class="mt-0.5 font-num text-sm">{{ port.vcom }} V</div>
                            </div>
                            <div class="rounded-control border border-hairline bg-sunken px-3 py-2.5">
                              <div class="text-xs text-ink-3">{{ t('devices.view.hardware.waveform') }}</div>
                              <div class="mt-0.5 break-all font-num text-sm">{{ port.waveform }}</div>
                            </div>
                            <div class="rounded-control border border-hairline bg-sunken px-3 py-2.5">
                              <div class="text-xs text-ink-3">{{ t('devices.view.hardware.epdFrequency') }}</div>
                              <div class="mt-0.5 font-num text-sm">{{ port.epdFrequency }} Hz</div>
                            </div>
                            <div class="rounded-control border border-hairline bg-sunken px-3 py-2.5">
                              <div class="text-xs text-ink-3">{{ t('devices.view.hardware.dimensions') }}</div>
                              <div class="mt-0.5 font-num text-sm">{{ port.width }} × {{ port.height }}</div>
                            </div>
                            <div class="rounded-control border border-hairline bg-sunken px-3 py-2.5">
                              <div class="text-xs text-ink-3">{{ t('devices.view.hardware.position') }}</div>
                              <div class="mt-0.5 font-num text-sm">{{ t('devices.view.hardware.rowCol', { row: port.rowNumber, col: port.columnNumber }) }}</div>
                            </div>
                            <div class="rounded-control border border-hairline bg-sunken px-3 py-2.5">
                              <div class="text-xs text-ink-3">{{ t('devices.view.hardware.orientation') }}</div>
                              <div class="mt-0.5 font-num text-sm">{{ port.reverseOrientation ? t('devices.view.hardware.reversed') : t('devices.view.hardware.normal') }}</div>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </section>
              </div>
            </div>
          }

          <!-- ───────── ACTIVITY ───────── -->
          @if (activeTab() === 'activity') {
            <div class="flex flex-col gap-6">
              <!-- snapshots -->
              <section class="rounded-card border border-hairline bg-card">
                <div class="flex items-center justify-between border-b border-hairline px-5 py-3.5">
                  <span class="text-sm font-semibold">{{ t('devices.view.snapshots.title') }}</span>
                  <button
                    type="button"
                    (click)="requestSnapshot()"
                    [disabled]="isRequestingSnapshot"
                    class="rounded-control border border-strong bg-card px-3 py-1.5 text-xs font-semibold hover:bg-sunken disabled:opacity-60"
                  >
                    {{ t('devices.view.liveImage.request') }}
                  </button>
                </div>
                <div class="grid grid-cols-2 gap-3.5 p-5 sm:grid-cols-4">
                  @for (snap of snapshots; track snap.id) {
                    <button
                      type="button"
                      (click)="viewSnapshot(snap)"
                      class="overflow-hidden rounded-control border border-hairline text-left hover:border-strong"
                    >
                      <div class="flex h-24 items-center justify-center border-b border-hairline bg-sunken">
                        @if (snapshotImageUrls[snap.id]) {
                          <img
                            [src]="snapshotImageUrls[snap.id]"
                            [alt]="t('devices.detail.latestImageAlt')"
                            class="h-full w-full object-cover"
                          />
                        } @else {
                          <span class="text-xs text-ink-3">{{ t('devices.view.snapshots.noThumbnail') }}</span>
                        }
                      </div>
                      <div class="flex items-center justify-between px-3 py-2">
                        <span class="font-num text-xs text-ink-2">{{
                          (snap.uploadedAt ?? snap.requestedAt) | date: 'short'
                        }}</span>
                        <span
                          class="text-xs font-semibold"
                          [class]="
                            {
                              ok: 'text-success',
                              failed: 'text-danger',
                              pending: 'text-warning',
                              neutral: 'text-ink-3',
                            }[messageTone(snap.status === 'UPLOADED' ? 'OK' : snap.status)]
                          "
                          >{{ snap.status }}</span
                        >
                      </div>
                    </button>
                  } @empty {
                    <div class="col-span-full py-8 text-center text-sm text-ink-3">
                      {{ t('devices.view.snapshots.empty') }}
                    </div>
                  }
                </div>
                @if (snapshotPageNumbers.length > 1) {
                  <div class="flex items-center justify-end gap-2 border-t border-hairline px-5 py-3">
                    <button
                      type="button"
                      (click)="prevSnapshotPage()"
                      [disabled]="snapshotPage === 0"
                      class="rounded-control border border-strong px-2.5 py-1 text-xs font-semibold disabled:opacity-40 hover:bg-ink-1/10"
                    >
                      {{ t('table.previous') }}
                    </button>
                    <span class="font-num text-xs text-ink-3">{{ snapshotPage + 1 }}</span>
                    <button
                      type="button"
                      (click)="nextSnapshotPage()"
                      [disabled]="!snapshotHasMore"
                      class="rounded-control border border-strong px-2.5 py-1 text-xs font-semibold disabled:opacity-40 hover:bg-ink-1/10"
                    >
                      {{ t('table.next') }}
                    </button>
                  </div>
                }
              </section>

              <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_1.4fr]">
                <!-- logs -->
                <section class="rounded-card border border-hairline bg-card">
                  <div class="flex items-center justify-between border-b border-hairline px-5 py-3.5">
                    <span class="text-sm font-semibold">{{ t('devices.view.logs.title') }}</span>
                    <div class="flex items-center gap-2">
                      <select
                        [(ngModel)]="selectedLogType"
                        class="rounded-control border border-strong bg-card px-2 py-1 text-xs"
                        [attr.aria-label]="t('devices.view.logs.type')"
                      >
                        @for (lt of logTypes; track lt) {
                          <option [value]="lt">{{ lt }}</option>
                        }
                      </select>
                      <button
                        type="button"
                        (click)="requestLogs()"
                        [disabled]="isRequestingLogs"
                        class="rounded-control border border-strong bg-card px-3 py-1.5 text-xs font-semibold hover:bg-sunken disabled:opacity-60"
                      >
                        {{ t('devices.view.logs.request') }}
                      </button>
                    </div>
                  </div>
                  <div class="px-5 py-1.5">
                    @for (log of pagedLogs; track log.id) {
                      <div
                        class="grid grid-cols-[64px_1fr_auto_auto] items-center gap-3.5 border-b border-hairline py-2.5 last:border-b-0"
                      >
                        <span
                          class="rounded border border-hairline bg-sunken py-0.5 text-center text-xs font-semibold text-ink-2"
                          >{{ log.logType }}</span
                        >
                        <span class="text-xs text-ink-3"
                          >{{ log.collectedFrom | date: 'short' }} – {{ log.collectedTo | date: 'short' }}</span
                        >
                        <span class="font-num text-xs text-ink-3">{{ formatSize(log.size) }}</span>
                        @if (log.status === 'UPLOADED') {
                          <button
                            type="button"
                            (click)="downloadLog(log)"
                            [disabled]="downloadingLogId === log.id"
                            class="text-xs font-semibold text-brand hover:underline disabled:opacity-50"
                          >
                            {{ t('devices.view.logs.download') }}
                          </button>
                        } @else {
                          <span
                            class="text-xs font-semibold"
                            [class]="log.status === 'FAILED' ? 'text-danger' : 'text-warning'"
                            >{{ log.status }}</span
                          >
                        }
                      </div>
                    } @empty {
                      <div class="py-8 text-center text-sm text-ink-3">{{ t('devices.view.logs.empty') }}</div>
                    }
                  </div>
                  @if (logs.length > activityPageSize) {
                    <div class="flex items-center justify-end gap-2 border-t border-hairline px-5 py-3">
                      <button
                        type="button"
                        (click)="prevLogsPage()"
                        [disabled]="logsPage === 0"
                        class="rounded-control border border-strong px-2.5 py-1 text-xs font-semibold disabled:opacity-40 hover:bg-ink-1/10"
                      >
                        {{ t('table.previous') }}
                      </button>
                      <span class="font-num text-xs text-ink-3">{{ logsPage + 1 }} / {{ logsPageCount }}</span>
                      <button
                        type="button"
                        (click)="nextLogsPage()"
                        [disabled]="logsPage >= logsPageCount - 1"
                        class="rounded-control border border-strong px-2.5 py-1 text-xs font-semibold disabled:opacity-40 hover:bg-ink-1/10"
                      >
                        {{ t('table.next') }}
                      </button>
                    </div>
                  }
                </section>

                <!-- messages -->
                <section class="rounded-card border border-hairline bg-card">
                  <!-- min-h matches the logs header, whose height is set by its controls -->
                  <div class="flex min-h-[58px] items-center border-b border-hairline px-5 text-sm font-semibold">
                    {{ t('devices.view.messages.title') }}
                  </div>
                  <div class="px-5 py-1.5">
                    @for (msg of pagedMessages; track msg.id) {
                      <div
                        class="grid grid-cols-[110px_1fr_auto] items-center gap-3.5 border-b border-hairline py-2.5 last:border-b-0"
                      >
                        <span class="font-num text-xs text-ink-3">{{ msg.created | date: 'short' }}</span>
                        <span class="text-sm"
                          >{{ msg.messageType }}
                          @if (msg.errorMessage) {
                            <span class="block text-xs text-danger">{{ msg.errorMessage }}</span>
                          }
                        </span>
                        <span
                          class="rounded px-2.5 py-1 text-xs font-bold tracking-wide"
                          [class]="
                            {
                              ok: 'bg-success-surface text-success',
                              failed: 'bg-danger-surface text-danger',
                              pending: 'bg-warning-surface text-warning',
                              neutral: 'bg-sunken text-ink-2',
                            }[messageTone(msg.messageStatus)]
                          "
                          >{{ msg.messageStatus }}</span
                        >
                      </div>
                    } @empty {
                      <div class="py-8 text-center text-sm text-ink-3">{{ t('devices.view.messages.empty') }}</div>
                    }
                  </div>
                  @if (messages.length > activityPageSize) {
                    <div class="flex items-center justify-end gap-2 border-t border-hairline px-5 py-3">
                      <button
                        type="button"
                        (click)="prevMessagesPage()"
                        [disabled]="messagesPage === 0"
                        class="rounded-control border border-strong px-2.5 py-1 text-xs font-semibold disabled:opacity-40 hover:bg-ink-1/10"
                      >
                        {{ t('table.previous') }}
                      </button>
                      <span class="font-num text-xs text-ink-3">{{ messagesPage + 1 }} / {{ messagesPageCount }}</span>
                      <button
                        type="button"
                        (click)="nextMessagesPage()"
                        [disabled]="messagesPage >= messagesPageCount - 1"
                        class="rounded-control border border-strong px-2.5 py-1 text-xs font-semibold disabled:opacity-40 hover:bg-ink-1/10"
                      >
                        {{ t('table.next') }}
                      </button>
                    </div>
                  }
                </section>
              </div>
            </div>
          }
        }
      </div>
    </div>

    <!-- ═══════════════ RESET MODAL ═══════════════ -->
    @if (resetModalOpen()) {
      <div
        class="fixed inset-0 z-[1100] flex items-center justify-center bg-scrim p-4"
        (click)="closeResetModal()"
        aria-hidden="true"
      >
        <div
          class="flex w-[420px] max-w-full flex-col gap-3.5 rounded-card bg-card p-7 shadow-overlay"
          role="dialog"
          aria-modal="true"
          (click)="$event.stopPropagation()"
        >
          <span class="text-lg font-bold">{{ t('devices.view.resetModal.title', { id: device.clientId }) }}</span>
          <p class="m-0 text-sm leading-relaxed text-ink-2">{{ t('devices.view.resetModal.body') }}</p>
          <label class="flex flex-col gap-1.5 text-sm">
            <span class="font-semibold">{{ t('devices.view.resetModal.type') }}</span>
            <select [(ngModel)]="selectedResetType" class="rounded-control border border-strong bg-card px-3 py-2">
              @for (rt of resetTypes; track rt) {
                <option [value]="rt">{{ t('devices.view.resetModal.types.' + rt) }}</option>
              }
            </select>
          </label>
          <div class="mt-1.5 flex justify-end gap-2.5">
            <button
              type="button"
              (click)="closeResetModal()"
              class="rounded-control border border-strong bg-card px-4 py-2 text-sm font-semibold hover:bg-sunken"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="button"
              (click)="confirmReset()"
              [disabled]="isResetting"
              class="rounded-control bg-danger px-4 py-2 text-sm font-semibold text-bar-ink hover:opacity-90 disabled:opacity-60"
            >
              {{ t('devices.view.actions.reset') }}
            </button>
          </div>
        </div>
      </div>
    }

    <!-- ═══════════════ REMOVE MODAL ═══════════════ -->
    @if (removeModalOpen()) {
      <div
        class="fixed inset-0 z-[1100] flex items-center justify-center bg-scrim p-4"
        (click)="closeRemoveModal()"
        aria-hidden="true"
      >
        <div
          class="flex w-[420px] max-w-full flex-col gap-3.5 rounded-card bg-card p-7 shadow-overlay"
          role="dialog"
          aria-modal="true"
          (click)="$event.stopPropagation()"
        >
          <span class="text-lg font-bold">{{ t('devices.view.removeModal.title', { id: device.clientId }) }}</span>
          <p class="m-0 text-sm leading-relaxed text-ink-2">{{ t('devices.view.removeModal.body') }}</p>
          <div class="mt-1.5 flex justify-end gap-2.5">
            <button
              type="button"
              (click)="closeRemoveModal()"
              class="rounded-control border border-strong bg-card px-4 py-2 text-sm font-semibold hover:bg-sunken"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="button"
              (click)="confirmRemove()"
              [disabled]="isRemoving"
              class="rounded-control bg-danger px-4 py-2 text-sm font-semibold text-bar-ink hover:opacity-90 disabled:opacity-60"
            >
              {{ t('devices.view.actions.remove') }}
            </button>
          </div>
        </div>
      </div>
    }

    <!-- ═══════════════ ASSIGN CONTENT DIALOG ═══════════════ -->
    @if (assignContentDialogOpen()) {
      <app-assign-content-dialog [device]="device" (close)="onAssignContentClosed()" />
    }

    <!-- ═══════════════ SNAPSHOT LIGHTBOX ═══════════════ -->
    @if (viewingSnapshot && snapshotImageUrls[viewingSnapshot.id]) {
      <div
        class="fixed inset-0 z-[1100] flex items-center justify-center bg-scrim p-8"
        (click)="closeSnapshotViewer()"
        aria-hidden="true"
      >
        <img
          [src]="snapshotImageUrls[viewingSnapshot.id]"
          [alt]="t('devices.detail.latestImageAlt')"
          class="max-h-full max-w-full rounded object-contain"
        />
      </div>
    }
  } @else if (isLoadingDevice) {
    <div class="flex min-h-full items-center justify-center text-ink-3">
      {{ t('devices.view.loading') }}
    </div>
  } @else {
    <div class="flex min-h-full flex-col items-center justify-center gap-3 text-ink-3">
      <span>{{ t('devices.view.notFound') }}</span>
      <button
        type="button"
        (click)="goBack()"
        class="rounded-control border border-hairline bg-card px-4 py-2 text-[13px] font-semibold text-ink-1 hover:bg-sunken"
      >
        {{ t('devices.actions.backToList') }}
      </button>
    </div>
  }

  <!-- Shared definition-list card -->
  <ng-template #detailCard let-title="title" let-rows="rows">
    <section class="rounded-card border border-hairline bg-card">
      <div class="border-b border-hairline px-5 py-3.5 text-sm font-semibold">{{ title }}</div>
      <div class="px-5 pb-3.5 pt-2">
        @for (row of rows; track row.key) {
          <div class="flex justify-between gap-4 border-b border-hairline py-2.5 text-sm last:border-b-0">
            <span class="text-ink-3">{{ row.key }}</span>
            <span class="break-all text-right font-num">{{ row.value }}</span>
          </div>
        }
      </div>
    </section>
  </ng-template>
</ng-container>
